import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import { z } from 'zod';
import AuthContext from '../../../contexts/AuthContext';
import TokenListContext from '../../../contexts/TokenListContext';
import useAPI from '../../../hooks/useAPI';
import { formatCurrency } from '../../../utility/number.utils';
import Loader from '../../common/Loader';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';

type MintTokenModalProps = {
  open: boolean;
  onClose: (withSuccess?: boolean) => void;
  tokenId?: string | undefined;
  refetchTransactionHistory?: () => void;
};

type FormData = z.infer<typeof schema>;

const schema = z.object({
  amount: z
    .string()
    .min(1, 'Total tokens must be greater than 0')
    .refine(val => Number(val) !== 0, {
      message: 'Enter a valid number of tokens',
    }),
});

const MintTokenModal = ({
  onClose,
  open,
  tokenId,
  refetchTransactionHistory,
}: MintTokenModalProps) => {
  const [formData, setFormData] = useState({
    amount: '',
    receiveFrom: '1',
  });
  const [showSuccess, setShow] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>();
  const { adminBalance } = useContext(AuthContext);
  const { refetchTransactionData, tokenList } = useContext(TokenListContext);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const {
    refetch: mintToken,
    data,
    isLoading,
  } = useAPI({
    url: '/token/mint/update',
    method: 'POST',
    onSuccess: data => {
      setShow(true);
      setTransactionId(data.result.data.data.transactionId);
      refetchTransactionHistory && refetchTransactionHistory();
      reset();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const handleMintToken = (data: FormData) => {
    const payload = {
      amount: data.amount,
      tokenId: tokenId,
    };

    if (!isLoading) mintToken(payload);
  };

  if (open && showSuccess)
    return (
      <TransactionSuccessModal
        open={showSuccess}
        title="Token minted successfully"
        onClose={() => {
          onClose(true);
          setShow(false);
        }}
        isCloseIconVisible={true}
      />
    );

  return (
    <Modal
      open={open}
      title="Mint Tokens"
      onClose={onClose}
      dialogClassName={styles.dialog_content}
      isCloseIconVisible={!isLoading}
    >
      <form onSubmit={handleSubmit(handleMintToken)}>
        <div className={styles.mintWrapper}>
          <div className={styles.mintInner}>
            <div className={styles.mintItem}>
              <label>Token Name</label>
              <span>
                {tokenList?.[0]?.tokenName || '-'}(
                {tokenList?.[0]?.tokenSymbol || '-'})
              </span>
            </div>
            <div className={styles.mintItem}>
              <label>Available Tokens</label>
              <span>
                {formatCurrency(adminBalance?.UserBalance ?? 0, {
                  currency: '',
                  decimals: 0,
                })}
              </span>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="" className={styles.label}>
              Enter Amount <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <div style={{ position: 'relative' }}>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={0}
                    allowNegative={false}
                    customInput={Input}
                    onValueChange={value => field.onChange(value.value)}
                    error={errors.amount?.message as string}
                    style={{
                      paddingRight: tokenList?.[0]?.tokenSymbol
                        ? '60px'
                        : '10px',
                    }}
                  />
                  {tokenList?.[0]?.tokenSymbol && (
                    <span
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        fontWeight: 600,
                        pointerEvents: 'none',
                      }}
                    >
                      {tokenList[0].tokenSymbol}
                    </span>
                  )}
                </div>
              )}
            />
          </Form.Group>
        </div>

        <div className={styles.actions}>
          <Button
            disabled={isLoading}
            variant="outline"
            onClick={() => onClose()}
            type="button"
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            variant="dark"
            type="submit"
            className={styles.delete_btn}
          >
            {isLoading ? <Loader /> : 'Confirm'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MintTokenModal;
